/**
 * MyWebsitesPage — /my/websites
 *
 * Owner dashboard: list of hosted sites with quick actions (edit,
 * open public URL, publish/unpublish, delete).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Globe, Sparkles, ExternalLink, Edit3, PlusCircle, Loader2, Eye, EyeOff, Trash2,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function MyWebsitesPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/websites/mine")
      .then(r => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Sign in to see your websites.");
      nav(`/login?redirect=${encodeURIComponent("/my/websites")}`, { replace: true });
      return;
    }
    if (user) load();
  }, [authLoading, user, nav]);

  const togglePublish = async (site) => {
    try {
      if (site.status === "published") await http.post(`/websites/${site.id}/unpublish`);
      else                              await http.post(`/websites/${site.id}/publish`);
      toast.success(site.status === "published" ? "Unpublished" : "Published!");
      load();
    } catch (err) { toast.error("Failed"); }
  };

  const remove = async (site) => {
    if (!confirm(`Delete "${site.name}"? This can't be undone.`)) return;
    try { await http.delete(`/websites/${site.id}`); toast.success("Deleted."); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="my-websites-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="overline mb-1">MY WEBSITES</div>
          <h1 className="font-display text-4xl mb-1 inline-flex items-center gap-2"><Globe className="w-8 h-8" />Your websites</h1>
          <p className="opacity-70">Manage every Instant Website you've built on Social Pulse.</p>
        </div>
        <Link to="/websites/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-new-website">
          <PlusCircle className="w-4 h-4" /> New website
        </Link>
      </div>

      {loading ? (
        <div className="opacity-60 inline-flex gap-2 items-center"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
      ) : items.length === 0 ? (
        <div className="wofy-card !p-8 text-center">
          <Sparkles className="w-8 h-8 mx-auto opacity-40 mb-3" />
          <p className="opacity-70 mb-3">No websites yet. Spin one up in under a minute.</p>
          <Link to="/websites/new" className="wofy-btn-primary inline-flex items-center gap-1.5">
            <PlusCircle className="w-4 h-4" />Build my first website
          </Link>
        </div>
      ) : (
        <ul className="grid gap-3">
          {items.map(s => (
            <li key={s.id} className="wofy-card !p-4 flex items-center gap-4 flex-wrap"
                data-testid={`site-row-${s.slug}`}>
              {s.logo_url ? (
                <img src={s.logo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-black/5 grid place-items-center text-lg font-mono">
                  {(s.name || "?")[0].toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{s.name}</div>
                <div className="text-xs opacity-70 truncate">
                  <code className="mr-2">/sites/{s.slug}</code>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${s.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                    {s.status}
                  </span>
                  <span className="ml-2">· {s.template} · {s.theme}</span>
                  <span className="ml-2 opacity-60">· {s.view_count || 0} views</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/websites/${s.id}/edit`} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5"
                  style={{ borderColor: "var(--wofy-border)" }} data-testid={`edit-${s.slug}`}>
                  <Edit3 className="w-3.5 h-3.5" />Edit
                </Link>
                {s.status === "published" ? (
                  <a href={`/sites/${s.slug}`} target="_blank" rel="noreferrer"
                     className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5"
                     style={{ borderColor: "var(--wofy-border)" }} data-testid={`open-${s.slug}`}>
                    <ExternalLink className="w-3.5 h-3.5" />Open
                  </a>
                ) : null}
                <button onClick={() => togglePublish(s)}
                  className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded border hover:bg-black/5"
                  style={{ borderColor: "var(--wofy-border)" }} data-testid={`toggle-${s.slug}`}>
                  {s.status === "published" ? <><EyeOff className="w-3.5 h-3.5" />Unpublish</> : <><Eye className="w-3.5 h-3.5" />Publish</>}
                </button>
                <button onClick={() => remove(s)} className="text-xs text-red-600 inline-flex items-center gap-0.5 hover:underline" data-testid={`delete-${s.slug}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
